/* eslint-disable react-hooks/exhaustive-deps */
// src/pages/profile/Profile.js
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import FloatingInput from "../../../components/input/FloatingInput";
import CustomSelect from "../../../components/customselect/CustomSelect";
import { yupResolver } from "@hookform/resolvers/yup";
import { ThemeContext } from "../../../context/ThemeContext";
import { profileRegisterSchema } from "../../../utils/registerSchema";
import { useModal } from "../../../context/ModalContext";
import api from "../../../api/axios";
import { useTranslation } from "react-i18next";

import "../../profile/profile.css";

export default function AddNewUserModel({ handleUpdateToaster, loadUserList }) {
  const { t: trans } = useTranslation();
  const theme = useContext(ThemeContext);
  const { closeModal } = useModal();
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [titleReps, setTitleReps] = useState([]);
  const mainCompanyId = theme.id;

  const schema = useMemo(() => profileRegisterSchema(states, counties, titleReps, trans), [
    states,
    counties,
    titleReps,
    trans,
  ]);

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    control,
    setError,
    setValue,
    getValues,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      associated_rep: "Unassigned",
      titlerep: "",
    },
    mode: "onSubmit",
  });

  const watchState = watch("state");
  const watchCounty = watch("county");
  const watchPostal = watch("postal_code");
  const [selectedCountyId, setSelectedCountyId] = useState(null)

  /** ✅ Dropdowns */
  useEffect(() => {
    api
      .get(`/auth/states`, { params: { maincompanyid: mainCompanyId } })
      .then((res) => {
        const fetchedStates = res.data.data || [];
        const selectOption = { id: "", statename: trans("register_state") };
        setStates([selectOption, ...fetchedStates]);

      })
      .catch((err) => console.error("Failed to fetch states:", err));
  }, [mainCompanyId]);

  // ✅ Fetch counties
  useEffect(() => {
    if (!watchState) {
      setCounties([]);
      return;
    }
    api
      .get(`/auth/counties`, { params: { maincompanyid: theme.id, stateId: watchState } })
      .then((res) => {
        const fetchedCounties = res.data.data || [];
        const selectOption = { id: "", countyname: trans("register_county") };
        setCounties([selectOption, ...fetchedCounties]);
      })
      .catch((err) => console.error("Failed to fetch counties:", err));
  }, [watchState, theme.id, trans]);

  useEffect(() => {
    if (selectedCountyId && counties.length) {
      setValue("county", selectedCountyId);
    }
  }, [counties]);

  // ✅ Revalidate ZIP when necessary
  useEffect(() => {
    if (watchCounty && watchPostal) {
      trigger("postal_code");
    }
  }, [watchCounty, watchPostal, trigger]);

  // ✅ Fetch title reps
  useEffect(() => {
    if (!watchState || !watchCounty) {
      setTitleReps([]);
      return;
    }
    api
      .get(`/auth/titlereps`, {
        params: { maincompanyid: theme.id, stateId: watchState, countyId: watchCounty },
      })
      .then((res) => {
        const fetchedReps = res.data.data || [];
        const selectOption = { id: "", name: trans("register_title_rep") };
        setTitleReps([selectOption, ...fetchedReps]);
      })
      .catch((err) => {
        console.error("Error fetching title reps:", err);
        setTitleReps([]);
      });
  }, [watchState, watchCounty, theme.id, trans]);


  // ✅ Blur validation handlers
  const zipControllerRef = useRef(null);
  const emailValidRef = useRef(null);
  const usernameValidRef = useRef(null);
  const postalValidRef = useRef(null);

  const handleEmailBlur = async (eOrValue) => {
    const email = typeof eOrValue === "string" ? eOrValue : eOrValue.target?.value;
    if (!email) return;
    try {
      const res = await api.get("/auth/check-email", { params: { email, maincompanyid: theme.id, user_type: "N" } });
      if (res.data?.status !== "success") {
        emailValidRef.current = false;
        setError("email", { type: "manual", message: trans("register_email_exists") });
      } else {
        emailValidRef.current = true;
        clearErrors("email");
      }
    } catch {
      setError("email", { type: "manual", message: trans("register_email_validation_failed") });
    }
  };

  const handleUsernameBlur = async (eOrValue) => {
    const username = typeof eOrValue === "string" ? eOrValue : eOrValue.target?.value;
    if (!username) return;
    try {
      const res = await api.get("/auth/check-username", { params: { username, maincompanyid: theme.id } });
      if (res.data?.status !== "success") {
        usernameValidRef.current = false;
        setError("username", { type: "manual", message: trans("register_username_exists") });
      } else {
        usernameValidRef.current = true;
        clearErrors("username");
      }
    } catch {
      setError("username", { type: "manual", message: trans("register_username_validation_failed") });
    }
  };

  const handlePostalBlur = async (eOrValue) => {
    const zip = typeof eOrValue === "string" ? eOrValue : eOrValue.target?.value;
    if (!zip) return;

    if (zipControllerRef.current) {
      try {
        zipControllerRef.current.abort();
      } catch { }
    }

    const controller = new AbortController();
    zipControllerRef.current = controller;

    const stateid = getValues("state");
    const countyid = getValues("county") || selectedCountyId;

    try {
      const res = await api.post(
        "/auth/verify-zip",
        { zipcode: zip, maincompanyid: theme.id, stateid, countyid },
        { signal: controller.signal }
      );
      if (res.data?.status === "fail") {
        setError("postal_code", { type: "manual", message: trans("register_zip_invalid") });
      } else {
        const state = res.data?.data?.stateid;
        const county = res.data?.data?.countyid;
        setValue('state', '');
        setTimeout(() => {
          setValue('state', state);
          setSelectedCountyId(county);
          clearErrors("postal_code");
        }, 100)
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError("postal_code", { type: "manual", message: trans("register_zip_validation_failed") });
      }
    }
  };

  // ✅ Field bindings
  const emailReg = register("email");
  const usernameReg = register("username");
  const postalReg = register("postal_code");

  const onSubmit = async (data) => {
    const stateObj = states.find((s) => s.id === parseInt(data.state));
    const countyObj = counties.find((c) => c.id === parseInt(data.county));

    const payload = {
      ...data,
      mainid: theme.id,
      gender: "M",
      state_name: stateObj?.statename,
      county_name: countyObj?.countyname,
    };

    try {
      const res = await api.post("/auth/register", payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 201) {
        // navigate("/register/success", { state: { fromRegister: true } });
        handleUpdateToaster({ message: "User registered successfully!", visible: true, type: 'success' });
        loadUserList();
        closeModal();
      } else {
        handleUpdateToaster({ message: "User registration failed!", visible: true, type: 'error' });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      handleUpdateToaster({
        message: "Unexpected response received!",
        visible: true,
        type: 'warning'
      });
    }
  };
  const formatMobile = (input) => {
    const digits = input.replace(/[^0-9]/g, "").slice(0, 20);
    let formatted = "";
    for (let i = 0; i < digits.length; i++) {
      if (i === 3 || i === 6) formatted += " - ";
      formatted += digits[i];
    }
    return formatted;
  };

  const handlePhoneChange = (value) => {
    const formatted = formatMobile(value);
    setValue('phone', formatted);
  };

  /** ✅ Render */
  return (
    <>
      <div className="user-p1 popupinner-wdth">
        <div className="user-form1">
          {/*  <div className="user-upload-img">
            <div className="user-info">
              <div className="user-p-pics">
                <div className="uploader ">
                  <input id="imgInput" type="file" accept="image/*" hidden onChange={handleImageChange} />
                  <label htmlFor="imgInput" className="upload-box" aria-label="Upload image">
                    {!isUploading && (
                      <div className="upload-hint">
                        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                          <path d="M12 16v-8m0 0l-3 3m3-3l3 3M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Upload</span>
                      </div>
                    )}
                  </label>
                  <button className="edit-up-img" onClick={handleImageSelect} >
                    Click here to change the image
                  </button>
                </div>
              </div>
              <div class="user-p-name"><h3>Welcome, Mayank 123</h3></div>
              <div class="user-p-desc"><p>Click on image to change. This image will appear on your net sheets.</p></div>
            </div>
          </div>*/}
          <div className="popupinner-wdth-a">
            <div className="popupinner-wdth-b">
              <div className="reg-f-container">
                <form
                  onSubmit={handleSubmit(async (data) => {
                    if (
                      emailValidRef.current === false ||
                      usernameValidRef.current === false ||
                      postalValidRef.current === false
                    ) {
                      if (emailValidRef.current === false) {
                        setError("email", { type: "manual", message: trans("register_email_exists") });
                      }
                      if (usernameValidRef.current === false) {
                        setError("username", { type: "manual", message: trans("register_username_exists") });
                      }
                      if (postalValidRef.current === false) {
                        setError("postal_code", { type: "manual", message: trans("register_zip_invalid") });
                      }
                      return;
                    }

                    const valid = await trigger();
                    if (!valid) return;
                    if (errors && Object.keys(errors).length > 0) return;

                    await onSubmit(data);
                  })}
                >
                  {/*   <div className="frm-field-2">
                    <div className="form-group">
                      <Controller
                        name="associated_rep"
                        control={control}
                        render={({ field }) => (
                          <FloatingInput
                            label="Associated rep"
                            {...field}
                            readOnly
                          />
                        )}
                      />

                    </div>
                    <div className="form-group assign-rep-btn">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          openModal({
                            title: "Associate to Rep",
                            size: "halffull",
                            content: (
                              <AssignRepModel
                                closeModal={closeModal}
                                onSelect={(rep) => {
                                  console.log("Selected rep:", rep);

                                  // Set field values inside AddNewUserModel form
                                  setValue("associated_rep", rep.name);
                                  setValue("titlerep", rep.id);
                                }}
                              />
                            )
                          });
                        }}
                      >
                        Assign Rep
                      </button>
                    </div>
                  </div>*/}

                  {/* First & Last Name */}
                  <div className="frm-field-2">
                    <div className="form-group">
                      <FloatingInput label={trans("register_first_name")} {...register("first_name")} />
                      {errors.first_name && <p className="field-error">{errors.first_name.message}</p>}
                    </div>
                    <div className="form-group">
                      <FloatingInput label={trans("register_last_name")} {...register("last_name")} />
                      {errors.last_name && <p className="field-error">{errors.last_name.message}</p>}
                    </div>
                  </div>

                  {/* Email Fields */}
                  <div className="frm-field-2">
                    <div className="form-group">
                      <FloatingInput label={trans("register_email")} {...emailReg} onBlur={handleEmailBlur} />
                      {errors.email && <p className="field-error">{errors.email.message}</p>}
                    </div>
                    {/* Username */}
                    <div className="form-group">
                      <FloatingInput label={trans("register_username")} {...usernameReg} onBlur={handleUsernameBlur} />
                      {errors.username && <p className="field-error">{errors.username.message}</p>}
                    </div>
                  </div>

                  {/* Address */}
                  <div className="frm-field-2">
                    <div className="form-group">
                      <FloatingInput label={trans("register_address")} {...register("mailing_address")} />
                      {errors.mailing_address && <p className="field-error">{errors.mailing_address.message}</p>}
                    </div>
                    <div className="form-group">
                      <FloatingInput label={trans("register_city")} {...register("city")} />
                      {errors.city && <p className="field-error">{errors.city.message}</p>}
                    </div>
                  </div>

                  {/* State & County */}
                  <div className="frm-field-2">
                    <div className="form-group">
                      <Controller
                        name="state"
                        control={control}
                        render={({ field }) => (
                          <CustomSelect options={states} 
                          label={"State"}
                          placeholder={trans("register_state")} onChange={field.onChange} value={field.value} />
                        )}
                      />
                      {errors.state && <p className="field-error">{errors.state.message}</p>}
                    </div>
                    <div className="form-group">
                      <Controller
                        name="county"
                        control={control}
                        render={({ field }) => (
                          <CustomSelect options={counties} 
                          label={"County"}
                          placeholder={trans("register_county")} onChange={field.onChange} value={field.value} />
                        )}
                      />
                      {errors.county && <p className="field-error">{errors.county.message}</p>}
                    </div>
                  </div>

                  {/* ZIP & Phone */}
                  <div className="frm-field-2">
                    <div className="form-group">
                      <FloatingInput label={trans("register_zip")} {...postalReg} onBlur={handlePostalBlur} maxLength={5} />
                      {errors.postal_code && <p className="field-error">{errors.postal_code.message}</p>}
                    </div>
                    <div className="form-group">
                      <FloatingInput label={trans("register_phone")} {...register("phone")} onChange={(e) => handlePhoneChange(e?.target?.value)} />
                      {errors.phone && <p className="field-error">{errors.phone.message}</p>}
                    </div>
                  </div>

                  {/* Office & Title Rep */}
                  <div className="frm-field-2">
                    <div className="form-group">
                      <FloatingInput label={trans("register_office")} {...register("office_name")} />
                      {errors.office_name && <p className="field-error">{errors.office_name.message}</p>}
                    </div>
                    <div className="form-group">
                      <Controller
                        name="titlerep"
                        control={control}
                        render={({ field }) => (
                          <CustomSelect options={titleReps} 
                          label={"Title Rep"}
                          placeholder={trans("register_title_rep")} onChange={field.onChange} value={field.value} />
                        )}
                      />
                      {errors.titlerep && <p className="field-error">{errors.titlerep.message}</p>}
                    </div>
                  </div>

                  {/* BRE */}
                  <div className="frm-field-2">
                    <div className="form-group">
                      <FloatingInput label={trans("register_bre")} {...register("dre_nmls")} />
                    </div>
                    <div className="form-group">
                      <FloatingInput label={trans("register_corp_bre")} {...register("corporate_nmls")} />
                    </div>
                  </div>

                  <div className="full-width-container-btn">
                    <div className="left-btn-container">

                      <button type="button" className="submit-btn btn-bg inpt-btn w-120 discard-btn" onClick={closeModal} >
                        Discard
                      </button>
                    </div>
                    <div className="right-btn-container">
                      <button type="submit" className="submit-btn btn-bg inpt-btn w-190" style={{ backgroundColor: theme.primary_color }}>
                        {isSubmitting ? "Saving..." : "Add user"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
